import React, { createContext, useContext, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { DEFAULT_LANGUAGE, STORAGE_KEY, SUPPORTED, baseLang } from "./index";

/**
 * URL scheme: English lives at the root (/about), other languages are prefixed (/hu/about, /sr/about).
 * The URL is the source of truth for the active language; the browser language / remembered choice only
 * decide where a first visit to a root URL is redirected to (client-side only, so crawlers always see English at /).
 */
const LangContext = createContext({ lang: DEFAULT_LANGUAGE, prefix: "" });

export const prefixFor = (lang) => (lang === DEFAULT_LANGUAGE ? "" : `/${lang}`);

/** Strip a language prefix from a pathname: "/hu/about" -> "/about" */
export const stripLangPrefix = (pathname) => {
  const m = pathname.match(/^\/(hu|sr)(\/|$)/);
  return m ? pathname.slice(m[0].length - (m[2] ? 1 : 0)) || "/" : pathname;
};

export const useLang = () => useContext(LangContext);

/** Route-group layout: sets the i18n language from the URL and provides the prefix to links. */
export const LangLayout = ({ lang, children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // first visit to an English URL: send the visitor to their preferred language (remembered choice, else browser)
  useEffect(() => {
    if (lang !== DEFAULT_LANGUAGE) return;
    let preferred = null;
    try {
      preferred = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* storage blocked */
    }
    if (!preferred) {
      const browser = (navigator.languages || [navigator.language || ""]).map(baseLang).find((l) => l !== DEFAULT_LANGUAGE);
      preferred = browser || DEFAULT_LANGUAGE;
    }
    preferred = baseLang(preferred);
    if (preferred !== DEFAULT_LANGUAGE && SUPPORTED.includes(preferred)) {
      const rest = location.pathname === "/" ? "" : location.pathname;
      navigate(`${prefixFor(preferred)}${rest}${location.search}${location.hash}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep i18next in sync with the URL
  useEffect(() => {
    if (baseLang(i18n.language) !== lang) i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return <LangContext.Provider value={{ lang, prefix: prefixFor(lang) }}>{children || <Outlet />}</LangContext.Provider>;
};

/** Drop-in replacement for react-router's Link that keeps the current language prefix. */
export const Link = ({ to, ...rest }) => {
  const { prefix } = useLang();
  const href = typeof to === "string" && to.startsWith("/") ? `${prefix}${to === "/" && prefix ? "" : to}` || "/" : to;
  return <RouterLink to={href} {...rest} />;
};

/** navigate() that keeps the language prefix */
export const useLangNavigate = () => {
  const navigate = useNavigate();
  const { prefix } = useLang();
  return (to, opts) => navigate(typeof to === "string" && to.startsWith("/") ? `${prefix}${to}` : to, opts);
};

// re-export for convenience in pages that also need params
export { useParams };
